import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { EventTimeline, SpecialProperty } from "@/components/timeline";
import { NoteEventInternalResponseSchema, fetchNoteEventsInternal } from "@/pages/notes/shared/api";

interface NoteEventTimelineProps {
  noteId: string;
  createdAt?: string;
}

// Extract special properties that should be rendered as links
const getSpecialProperties = (properties: Record<string, unknown>): SpecialProperty[] => {
  const special: SpecialProperty[] = [];

  // Check for suggestion_id (from cleanup/refine complete events) or note_suggestion_id (from suggestion created events)
  const suggestionId = properties.suggestion_id ?? properties.note_suggestion_id;
  if (suggestionId && typeof suggestionId === "string") {
    special.push({
      key: "suggestion_id",
      label: "Note Suggestion",
      id: suggestionId,
      route: atlasRoutes.noteSuggestionsView({
        params: { noteSuggestionId: suggestionId },
      }).path,
    });
  }

  // Check for associated_session_id to link to session view
  if (properties.associated_session_id && typeof properties.associated_session_id === "string") {
    special.push({
      key: "associated_session_id",
      label: "Session",
      id: properties.associated_session_id,
      route: atlasRoutes.sessionsView({
        params: { sessionId: properties.associated_session_id },
      }).path,
    });
  }

  if (properties.audio_recording_id && typeof properties.audio_recording_id === "string") {
    special.push({
      key: "audio_recording_id",
      label: "Audio Recording",
      id: properties.audio_recording_id,
      route: atlasRoutes.audioRecordingsView({
        params: { audioRecordingId: properties.audio_recording_id },
      }).path,
    });
  }

  return special;
};

export const NoteEventTimeline = ({ noteId, createdAt }: NoteEventTimelineProps) => {
  const query = useQuery({
    queryKey: ["note-events", noteId],
    queryFn: () => fetchNoteEventsInternal(noteId),
    enabled: Boolean(noteId),
    staleTime: 30 * 1000,
  });

  const events = query.data?.events ?? [];
  const total = query.data?.total ?? 0;

  return (
    <EventTimeline<NoteEventInternalResponseSchema>
      events={events}
      total={total}
      isLoading={query.isLoading}
      isError={query.isError}
      createdAt={createdAt}
      createdLabel="Note Created"
      emptyMessage="No events recorded yet for this note."
      getSpecialProperties={getSpecialProperties}
    />
  );
};
